import { useContext } from 'react'

import Swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCopy, faHouse, faImage, faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import { Button, Tools } from '@domain-group/suit-theme'

import useAgencySelector from '@hooks/useAgencySelector'
import useImageLibrary from '@hooks/useImageLibrary'
import useTeam, { generateUrls } from '@hooks/useTeam'

import { confirmDelete } from '@functions/entity'

import { TeamModel } from '@models/team'
import { UserModel } from '@models/user'

import PageContext from '@contexts/pageContext'

interface SetTeamAsDefaultParams {
  leadAgent: UserModel,
  team: TeamModel,
  setAsDefault: (payload: object) => Promise<object>,
  setDefaultFn: (payload: object) => Promise<object>,
}

const setTeamAsDefault = (params: SetTeamAsDefaultParams) => {
  const {
    leadAgent,
    setAsDefault,
    setDefaultFn,
    team: {
      id,
    },
  } = params

  const setAsDefaultPayload = {
    callbacks: {
      action: setDefaultFn,
    },
    entityParams: { id, default: true },
    toastText: 'Team set as default',
  }
  Swal.fire({
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, set as default.',
    icon: 'warning',
    showCancelButton: true,
    text: 'Are you sure? Each agent can only have one default team.',
    title: `Set as default for ${leadAgent.full_name}?`,
  })
    .then(({ value }) => {
      if (value){
        setAsDefault(setAsDefaultPayload)
      }
    })
}

interface TeamToolsProps {
  teamPayload: ReturnType<typeof useTeam>,
}

const TeamTools = (props: TeamToolsProps) => {
  const { teamPayload } = props

  const {
    callbacks: {
      deleteTeam: deleteFn,
      launchDuplicateTeamModal,
      setAsDefault: setDefaultFn,
    },
    deleting,
    leadAgent,
    team,
    team: {
      default: isDefault,
      id,
      image_library_id,
    },
  } = teamPayload

  const imageLibraryPayload = useImageLibrary({ id: image_library_id, owner_id: id, owner_type: 'Team' })

  const {
    callbacks: {
      createOrEditImageLibrary,
    },
  } = imageLibraryPayload

  const { selectedAgencyId } = useAgencySelector()
  const { teamsIndexUrl } = generateUrls({}, { prefixRoute: `agencies/${selectedAgencyId}/manage` })

  const confirmDeletePayload = {
    callbacks: {
      afterAction: () => {
        window.location.hash = teamsIndexUrl
      },
      deleteFn,
    },
    strings: { entityName: 'Team' },
  }

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      setAsDefault,
    },
  } = pageContext

  return (
    <Tools>
      {!isDefault && (
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faHouse} />}
          // @ts-expect-error TS2322
          marginBottom="medium"
          onClick={() => setTeamAsDefault({
            // @ts-expect-error TS2740
            setDefaultFn, setAsDefault, team, leadAgent,
          })}
          size="medium"
        >
          Set As Default
        </Button>
      )}

      <Button
        buttonStyle="secondaryUtility"
        icon={<FontAwesomeIcon icon={faCopy} />}
        // @ts-expect-error TS2322
        marginBottom="medium"
        onClick={() => launchDuplicateTeamModal()}
        size="medium"
      >
        Duplicate Team
      </Button>

      <Button
        buttonStyle="secondaryUtility"
        icon={<FontAwesomeIcon icon={faImage} />}
        // @ts-expect-error TS2322
        marginBottom="medium"
        onClick={() => createOrEditImageLibrary()}
        size="medium"
      >
        Manage Images
      </Button>

      <Button
        buttonStyle="secondaryUtility"
        icon={<FontAwesomeIcon icon={faTrashAlt} />}
        loading={deleting}
        onClick={() => confirmDelete(confirmDeletePayload)}
        size="medium"
      >
        Delete Team
      </Button>
    </Tools>
  )
}

export default TeamTools
